import {
  Badge,
  Button,
  Field,
  Input,
  tokens,
  type InputProps,
} from "@fluentui/react-components";
import {
  ArrowReset20Regular,
  Open20Regular,
} from "@fluentui/react-icons";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { useUpdateDocument } from "@/features/review/useUpdateDocument";

export interface DocumentTradeKeyProps {
  documentId: string;
  tradeKey: string | null;
  tradeKeyOverride: string | null;
  emailTradeKey: string | null;
  /** SharePoint folder URL; when set, an "Open in SharePoint" button appears. */
  folderUrl?: string | null;
  readOnly?: boolean;
  /** Rendered inside the same bordered panel, beneath the input. */
  footer?: ReactNode;
}

/**
 * Per-document trade key editor shown at the top of the review layout.
 *
 * - When the doc has an override (``tradeKeyOverride !== null``), an
 *   "Override" badge is shown next to the field and a Reset button
 *   clears the override so the doc re-inherits from the email.
 * - When no override, the field shows the inherited value from the
 *   email and typing a different value applies an override on Update.
 */
export function DocumentTradeKey({
  documentId,
  tradeKey,
  tradeKeyOverride,
  emailTradeKey,
  folderUrl,
  readOnly,
  footer,
}: DocumentTradeKeyProps) {
  const update = useUpdateDocument(documentId);
  const [draft, setDraft] = useState<string>(tradeKey ?? "");

  useEffect(() => {
    setDraft(tradeKey ?? "");
  }, [tradeKey, documentId]);

  const hasOverride = tradeKeyOverride !== null;
  const normalizedDraft = draft.trim() || null;
  const dirty = normalizedDraft !== (tradeKey ?? null);

  const apply = () => {
    if (!dirty) return;
    update.mutate({ tradeKey: normalizedDraft });
  };

  const resetToParent = () => {
    if (!hasOverride) return;
    update.mutate({ tradeKey: null });
  };

  const onChange: InputProps["onChange"] = (_, d) => setDraft(d.value);

  const inputTitle = hasOverride
    ? `Overriding email trade ${emailTradeKey ?? "(uncategorized)"}. Format: S259347.1.1 (sale) or B259347.1.1 (purchase).`
    : emailTradeKey
      ? "Inherited from email. Type a different value to override. Format: S259347.1.1 (sale) or B259347.1.1 (purchase)."
      : "Email is uncategorized. Set a value to override for this document. Format: S259347.1.1 (sale) or B259347.1.1 (purchase).";

  return (
    <div
      style={{
        background: tokens.colorNeutralBackground1,
        padding: 12,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: 6,
        marginBottom: 12,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <Field
        label={
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            Trade key
            {hasOverride ? (
              <Badge
                appearance="filled"
                color="warning"
                size="small"
                aria-label="Overridden from email trade"
              >
                Override
              </Badge>
            ) : null}
          </span>
        }
        validationState={
          update.isError
            ? "error"
            : update.isSuccess && !dirty
              ? "success"
              : "none"
        }
        validationMessage={
          update.isError
            ? (update.error instanceof Error
                ? update.error.message
                : "Update failed")
            : update.isSuccess && !dirty
              ? "Updated"
              : undefined
        }
      >
        <div style={{ display: "flex", gap: 8 }}>
          <Input
            value={draft}
            onChange={onChange}
            placeholder="e.g. S259347.1.1"
            title={inputTitle}
            disabled={readOnly || update.isPending}
            onKeyDown={(e) => {
              if (e.key === "Enter" && dirty) apply();
            }}
            style={{ flex: 1 }}
          />
          <Button
            appearance="primary"
            onClick={apply}
            disabled={readOnly || !dirty || update.isPending}
          >
            {update.isPending ? "Updating…" : "Update"}
          </Button>
          {hasOverride ? (
            <Button
              icon={<ArrowReset20Regular />}
              onClick={resetToParent}
              disabled={readOnly || update.isPending}
              title="Reset to email's trade"
            >
              Reset
            </Button>
          ) : null}
          {folderUrl ? (
            <Button
              icon={<Open20Regular />}
              as="a"
              href={folderUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Open folder in SharePoint"
              aria-label="Open folder in SharePoint"
            />
          ) : null}
        </div>
      </Field>
      {footer}
    </div>
  );
}
